import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardShell from '@/components/DashboardShell';
import api, { getImageUrl } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, X, CreditCard, AlertCircle } from 'lucide-react';

import { compressImage } from '@/utils/imageCompression';

export default function CreateListing() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [listingTypes, setListingTypes] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', category_id: '', type_id: '', price: '', currency: 'GBP', year: '', dimensions: '', medium: '', condition: '', provenance: '', status: 'draft' });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    api.get('/listing-types').then(r => setListingTypes(r.data)).catch(() => {});
    api.get('/public/payment-config').then(r => setPaymentConfig(r.data)).catch(() => {});
  }, []);

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      setUploading(true);
      try {
        const compressed = await compressImage(file);
        const fd = new FormData();
        fd.append('file', compressed);
        const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setImages(prev => [...prev, r.data.url]);
      } catch (e) {
        toast.error('Failed to upload image');
      } finally {
        setUploading(false);
      }
    }
    e.target.value = '';
  };

  const removeImage = (url) => setImages(prev => prev.filter(u => u !== url));

  const paymentRequired = () => {
    if (!paymentConfig) return false;
    const fee = paymentConfig.listing_fee || 0;
    if (fee <= 0) return false;
    const methods = paymentConfig.active_payment_methods || {};
    return methods.stripe || methods.paypal;
  };

  const handleSubmit = async (e, publishStatus) => {
    e.preventDefault();
    if (!form.title || !form.category_id || !form.type_id) { toast.error('Please fill in all required fields'); return; }
    setSaving(true);
    try {
      // Always create as draft first
      const payload = {
        ...form,
        price: form.price ? parseFloat(form.price) : null,
        status: 'draft',
        images,
      };
      const res = await api.post('/my/listings', payload);
      const listingId = res.data.id;

      if (publishStatus === 'submit') {
        if (paymentRequired()) {
          // Payment needed → redirect to Stripe first; after payment it becomes pending_review
          const origin = window.location.origin;
          const checkoutRes = await api.post('/payments/stripe/checkout', { listing_id: listingId, origin_url: origin });
          window.location.href = checkoutRes.data.checkout_url;
        } else {
          // No payment → submit directly for admin review
          await api.post(`/my/listings/${listingId}/submit`);
          toast.success('Listing submitted for review. You will be notified once approved.');
          navigate('/dashboard/listings');
        }
      } else {
        toast.success('Listing saved as draft');
        navigate('/dashboard/listings');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create listing');
    } finally {
      setSaving(false);
    }
  };

  const CURRENCIES = ['GBP', 'EUR', 'USD'];
  const fee = paymentConfig?.listing_fee || 0;
  const stripeEnabled = paymentConfig?.active_payment_methods?.stripe;

  return (
    <DashboardShell title="Create Listing">
      <form className="max-w-2xl space-y-6" onSubmit={(e) => handleSubmit(e, form.status)}>
        {/* Images */}
        <div>
          <Label className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-muted)' }}>Images</Label>
          <div className="flex flex-wrap gap-3">
            {images.map((url, i) => (
              <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-clr)' }}>
                <img src={getImageUrl(url)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(url)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center">
                  <X size={10} style={{ color: 'white' }} />
                </button>
              </div>
            ))}
            <label className="w-20 h-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer" style={{ borderColor: 'var(--border-clr)' }}>
              {uploading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#1C1C1C] border-t-transparent" /> : <><Upload size={16} style={{ color: 'var(--text-muted)' }} /><span className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Add</span></>}
              <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageSelect} />
            </label>
          </div>
        </div>

        {/* Basic info */}
        <div className="space-y-4 p-5 rounded-xl border" style={{ borderColor: 'var(--border-clr)' }}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Basic Information</h3>
          <div>
            <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Title *</Label>
            <Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="e.g. Untitled No. 3 (2022)" required style={{ borderColor: 'var(--border-clr)' }} data-testid="listing-title" />
          </div>
          <div>
            <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Description</Label>
            <Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={3} placeholder="Describe the work..." style={{ borderColor: 'var(--border-clr)' }} data-testid="listing-description" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Category *</Label>
              <Select value={form.category_id} onValueChange={v => setForm(f => ({...f, category_id: v}))}>
                <SelectTrigger data-testid="listing-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Listing Type *</Label>
              <Select value={form.type_id} onValueChange={v => setForm(f => ({...f, type_id: v}))}>
                <SelectTrigger data-testid="listing-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{listingTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-4 p-5 rounded-xl border" style={{ borderColor: 'var(--border-clr)' }}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Artwork Details</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Price</Label>
              <Input type="number" value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))} placeholder="Leave blank for POA" style={{ borderColor: 'var(--border-clr)' }} data-testid="listing-price" />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Currency</Label>
              <Select value={form.currency} onValueChange={v => setForm(f => ({...f, currency: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Year</Label><Input value={form.year} onChange={e => setForm(f => ({...f, year: e.target.value}))} placeholder="2023" style={{ borderColor: 'var(--border-clr)' }} /></div>
            <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Dimensions</Label><Input value={form.dimensions} onChange={e => setForm(f => ({...f, dimensions: e.target.value}))} placeholder="120 x 80 cm" style={{ borderColor: 'var(--border-clr)' }} /></div>
            <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Medium</Label><Input value={form.medium} onChange={e => setForm(f => ({...f, medium: e.target.value}))} placeholder="Oil on canvas" style={{ borderColor: 'var(--border-clr)' }} /></div>
            <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Condition</Label><Input value={form.condition} onChange={e => setForm(f => ({...f, condition: e.target.value}))} placeholder="Excellent" style={{ borderColor: 'var(--border-clr)' }} /></div>
          </div>
          <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Provenance</Label><Textarea value={form.provenance} onChange={e => setForm(f => ({...f, provenance: e.target.value}))} rows={2} placeholder="Exhibition history, previous ownership..." style={{ borderColor: 'var(--border-clr)' }} /></div>
        </div>

        {/* Payment notice */}
        {paymentRequired() && (
          <div className="flex items-start gap-3 p-4 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--bg-subtle)' }} data-testid="payment-notice">
            <CreditCard size={16} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--text-muted)' }} />
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                Listing fee: £{fee.toFixed(2)}
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {stripeEnabled ? 'You will be redirected to Stripe to pay the listing fee. Your listing will then be submitted for admin review.' : 'Payment required to submit.'}
              </p>
            </div>
          </div>
        )}

        {!paymentRequired() && fee > 0 && (
          <div className="flex items-start gap-3 p-4 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--bg-subtle)' }}>
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--text-muted)' }} />
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              No payment gateway is currently enabled. Contact the admin to activate Stripe or PayPal.
            </p>
          </div>
        )}

        {!paymentRequired() && fee <= 0 && (
          <div className="flex items-start gap-3 p-4 rounded-xl border" style={{ borderColor: '#D1FAE5', backgroundColor: '#F0FDF4' }}>
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" style={{ color: '#065F46' }} />
            <p className="text-xs" style={{ color: '#065F46' }}>
              Submissions are reviewed by our team before going live. You will be notified by email once approved.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={(e) => handleSubmit(e, 'draft')}
            disabled={saving}
            style={{ borderColor: 'var(--border-strong)', borderRadius: '10px' }}
            data-testid="save-draft-btn"
          >
            Save as Draft
          </Button>
          <Button
            type="button"
            onClick={(e) => handleSubmit(e, 'submit')}
            disabled={saving || (fee > 0 && !paymentRequired())}
            style={{ backgroundColor: 'var(--text)', color: 'var(--bg)', borderRadius: '10px' }}
            data-testid="publish-btn"
          >
            {saving ? 'Processing…' : paymentRequired() ? `Pay £${fee.toFixed(2)} & Submit` : 'Submit for Review'}
          </Button>
        </div>
      </form>
    </DashboardShell>
  );
}
